import { defineStore } from 'pinia';
import { notify } from 'notiwind';
import * as Sentry from '@sentry/vue';

import { useDebounce } from '@/composables/useDebounce';
import { useWalletCache } from '@/composables/useWalletCache';
import dayjs from 'dayjs';
import { Apii } from '@/services/api.js';

const ApiService = new Apii();

// MY1-2830: client-side wallet cache (IndexedDB). Instantiated once per session
// via initWalletCache() with the authed user's UUID. Module-scoped rather than
// store state because it's a non-reactive handle, not serializable data.
let walletCache = null;

// SUPPORT-739: how old the in-memory shell may get before a tab that regains
// visibility revalidates it in the background. Long-lived tabs otherwise keep
// rendering the mount-time payload forever (bootstrapWallet only runs on
// mount), which is how a 13-day-old business list served deletable ghost rows.
const WALLET_SHELL_MAX_AGE_MS = 15 * 60 * 1000;

const CONNECTION_PROBLEM_STATES = [
    'CHALLENGED',
    'REJECTED',
    'EXPIRED',
    'DENIED',
    'FAILED',
    'DISCONNECTED',
    'IMPAIRED',
    'LOCKED',
    'PREVENTED',
    'IMPEDED',
    'DEGRADED',
    'DISCONTINUED',
    'CLOSED',
    'DISABLED',
];

export const useWalletStore = defineStore('wallet', {
    state: () => ({
        _debouncedSavePreferences: null,
        activeAccountDropdown: {},
        activeAccountDropdownElement: null,
        banks: [],
        cards: [],
        groupBy: {
            id: 'user_name',
            name: 'User',
        },
        subgroupBy: {
            id: 'institution_name',
            name: 'Bank',
        },
        hasConnections: null,
        institution: '',
        invitationBannerDismissed: false,
        loaded: false,
        loadingMembers: false,
        loadingWallet: false,
        refreshingWallet: false,
        benefitsLoading: false,
        // SUPPORT-739: epoch ms of the last time the in-memory shell matched a
        // known-fresh source (network fetch, or cache hydrate minus its age).
        // Drives revalidateWalletIfStale() for long-lived tabs.
        shellSyncedAt: null,
        members: [],
        pendingInvitations: [],
        newInvitation: null, // Store for newly created invitations
        selectedCard: null,
        selectedUser: null,
        accountToEdit: null,
        showAccountSidebar: false,
        showAddUserModal: false,
        showEditCardModal: false,
        showCopyCardModal: false,
        showCardDropdown: false,
        showProductChangeModal: false,
        editingProductChange: null,

        // business entities:
        businessEntity: {
            id: null,
            user_id: null,
            name: null,
            type: null,
            tax_id_type: null,
        },
        businessEntities: [],
        loadingBusinessEntities: false,
        showBusinessAddEditModal: false,
        showManageBusinessesModal: false,
        showDeleteConfirmModal: false,
        showCardMatchModal: false,
        cardMatchSkipToRequestForm: false,
        sortBy: 'card_title',
        sortDirection: 'asc',
        walletCards: [],
        walletEntities: [],
        wallet: {
            id: 0,
            user_id: 0,
        },
        walletId: 0,
        users: [],
        features: {},
        walletUsers: [],

        // MY1-3044: deep components (card details banner / bonus tip) ask the
        // wallet page to open the connect flow through this flag instead of
        // threading emits up the tree. The page watches and resets it.
        connectModalRequested: false,

        // Wallet settings
        groupSameCards: false,
        showHiddenCards: false,
        showLastFour: true,
        visibleColumns: [
            'title',
            'institution_name',
            'card_type',
            'user_name',
            'account_type',
            'credit_limit',
            'card_status',
            'date_opened',
            'date_closed',
            'annual_fee_computed',
            'welcome_bonus',
            'bonus_offers',
            'chase_524',
        ],
        columnOrder: [
            'title',
            'institution_name',
            'card_type',
            'user_name',
            'account_type',
            'credit_limit',
            'card_status',
            'date_opened',
            'date_closed',
            'annual_fee_computed',
            'welcome_bonus',
            'bonus_offers',
            'chase_524',
        ],
        selectedUsersOrEntities: [],
        selectedBanks: [],
        selectedCardTypes: [],
        selectedStatuses: [],
        selectedAccountTypes: [],
        selectedWelcomeBonusStatuses: [],
        selectedBonusOfferStatuses: [],
        selectedFiveTwentyFourStatuses: [],
        statuses: [
            { id: 'open', name: 'Open' },
            { id: 'closed', name: 'Closed' },
        ],
        columns: [
            {
                id: 'title',
                name: 'Card name',
                width: '300px',
                alignment: 'start',
            },
            {
                id: 'institution_name',
                name: 'Bank',
                width: '160px',
                alignment: 'start',
            },
            {
                id: 'card_type',
                name: 'Card type',
                width: '120px',
                alignment: 'start',
            },
            {
                id: 'user_name',
                name: 'Owner',
                width: '150px',
                alignment: 'start',
            },
            {
                id: 'account_type',
                name: 'Account type',
                width: '120px',
                alignment: 'start',
            },
            {
                id: 'credit_limit',
                name: 'Credit limit',
                width: '120px',
                alignment: 'end',
            },
            {
                id: 'card_status',
                name: 'Status',
                width: '110px',
                alignment: 'end',
            },
            {
                id: 'date_opened',
                name: 'Opened',
                width: '100px',
                alignment: 'start',
            },
            {
                id: 'date_closed',
                name: 'Closed',
                width: '100px',
                alignment: 'start',
            },
            {
                id: 'annual_fee_computed',
                name: 'Annual fee',
                width: '100px',
                alignment: 'end',
            },
            {
                id: 'welcome_bonus',
                name: 'Welcome bonus',
                // MY1-3041: pill stack + the 120px tracking bar block.
                width: '270px',
                alignment: 'start',
            },
            {
                id: 'bonus_offers',
                name: 'Bonus offers',
                width: '130px',
                alignment: 'start',
            },
            {
                id: 'chase_524',
                name: '5/24',
                width: '60px',
                alignment: 'start',
            },
            {
                id: 'actions',
                name: 'Actions',
                width: '60px',
                alignment: 'center',
            },
        ],
        groupingOptions: [
            {
                id: 'user_name',
                name: 'User',
            },
            {
                id: 'institution_name',
                name: 'Bank',
            },
            {
                id: 'card_status',
                name: 'Status',
            },
        ],
    }),
    getters: {
        myWallet: (state) => state.wallet,
        orderedColumns: (state) => {
            // Always keep title column separate to ensure it's in position 0
            const titleColumn = state.columns.find((col) => col.id === 'title');
            const orderedCols = [];

            // Validate and add columns (except title) in the specified order
            state.columnOrder.forEach((columnId) => {
                // Skip the title column as it will be handled separately
                if (columnId === 'title') return;

                const column = state.columns.find((col) => col.id === columnId);
                if (column) {
                    // Ensure all column properties are preserved
                    orderedCols.push({ ...column });
                } else {
                    // Log warning for missing column IDs (development aid)
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(
                            `Column ID '${columnId}' in columnOrder not found in columns array`
                        );
                    }
                }
            });

            // Add any columns (except title) that aren't in the order array (for backwards compatibility)
            state.columns.forEach((column) => {
                if (
                    column.id !== 'title' &&
                    !state.columnOrder.includes(column.id)
                ) {
                    orderedCols.push({ ...column });
                }
            });

            // Always place title column at the beginning (position 0)
            return titleColumn
                ? [{ ...titleColumn }, ...orderedCols]
                : orderedCols;
        },
        hasActiveFilters: (state) => {
            return (
                state.selectedUsersOrEntities.length > 0 ||
                state.selectedBanks.length > 0 ||
                state.selectedCardTypes.length > 0 ||
                state.selectedStatuses.length > 0 ||
                state.selectedAccountTypes.length > 0 ||
                state.selectedWelcomeBonusStatuses.length > 0 ||
                state.selectedBonusOfferStatuses.length > 0 ||
                state.selectedFiveTwentyFourStatuses.length > 0
            );
        },
        activeFiltersCount(state) {
            return (
                state.selectedUsersOrEntities.length +
                state.selectedBanks.length +
                state.selectedCardTypes.length +
                state.selectedStatuses.length +
                state.selectedAccountTypes.length +
                state.selectedWelcomeBonusStatuses.length +
                state.selectedBonusOfferStatuses.length +
                state.selectedFiveTwentyFourStatuses.length
            );
        },
        filteredCards(state) {
            let cards = state.walletCards;
            if (!state.showHiddenCards) {
                cards = cards.filter((card) => !card.ignore);
            }

            return this.applyAllFilters(cards);
        },
        visibleCards(state) {
            let cards = state.walletCards;
            if (!state.showHiddenCards) {
                cards = cards.filter((card) => !card.ignore);
            }
            let filtered = this.applyAllFilters(cards);
            let sorted = this.sortCards(filtered);
            if (!state.groupSameCards) return sorted;

            const groupedByCardId = this.groupByCardId(sorted);

            return groupedByCardId.map((group) => {
                return {
                    card_id: group.card_id,
                    cards: group.cards,
                };
            });
        },
        hiddenCardsCount(state) {
            let filtered = this.applyAllFilters(state.walletCards);

            return filtered.filter((card) => card.ignore).length;
        },
        groupedCards(state) {
            if (!state.groupBy?.id) return null;
            let cards = state.walletCards;
            if (!state.showHiddenCards) {
                cards = cards.filter((card) => !card.ignore);
            }
            let filtered = this.applyAllFilters(cards);
            let sorted = this.sortCards(filtered);
            const primaryGroups = sorted.reduce((groups, item) => {
                const rawValue = item[state.groupBy.id];
                const groupKey =
                    rawValue !== undefined && rawValue !== null
                        ? rawValue
                        : 'Ungrouped';

                if (!groups[groupKey]) groups[groupKey] = [];
                groups[groupKey].push(item);
                return groups;
            }, {});

            if (state.groupSameCards && !state.subgroupBy?.id) {
                Object.entries(primaryGroups).forEach(
                    ([groupKey, groupItems]) => {
                        primaryGroups[groupKey] =
                            this.groupByCardId(groupItems);
                    }
                );
            }
            if (!state.subgroupBy?.id) return primaryGroups;

            const result = {};

            Object.keys(primaryGroups).forEach((primaryKey) => {
                const primaryGroup = primaryGroups[primaryKey];
                result[primaryKey] = primaryGroup.reduce((subgroups, item) => {
                    const rawValue = item[state.subgroupBy.id];
                    const subgroupKey =
                        rawValue !== undefined && rawValue !== null
                            ? rawValue
                            : 'Ungrouped';

                    if (!subgroups[subgroupKey]) subgroups[subgroupKey] = [];
                    subgroups[subgroupKey].push(item);
                    return subgroups;
                }, {});

                if (state.groupSameCards) {
                    Object.entries(result[primaryKey]).forEach(
                        ([subGroupKey, subGroupItems]) => {
                            result[primaryKey][subGroupKey] =
                                this.groupByCardId(subGroupItems);
                        }
                    );
                }
            });

            return result;
        },
        walletUsersAndEntities(state) {
            return state.walletEntities.map((item) => ({
                id: item.id,
                name: item.name,
            }));
        },
        walletBanks() {
            // First, ensure walletEntities is valid
            if (!this.walletEntities || !Array.isArray(this.walletEntities)) {
                return [];
            }

            const banks = this.walletEntities.reduce((banks, user) => {
                // Skip if user has no institutions or institutions is not an array
                if (!user.institutions || !Array.isArray(user.institutions)) {
                    return banks;
                }

                user.institutions.forEach((institution) => {
                    // Skip invalid institutions
                    if (!institution) {
                        return;
                    }

                    if (
                        !banks.find(
                            (bank) => bank.id === institution.issuing_bank_id
                        )
                    ) {
                        // Get name with fallbacks
                        let name = 'Unknown Bank';

                        if (
                            institution.issuing_bank &&
                            institution.issuing_bank.name
                        ) {
                            name = institution.issuing_bank.name;
                        } else if (institution.name) {
                            name = institution.name;
                        }

                        banks.push({
                            id: institution.issuing_bank_id,
                            name,
                            image: institution.small_logo_url || null,
                        });
                    }
                });
                return banks;
            }, []);

            // Sort banks by name, with null-safety
            return banks.sort((a, b) => {
                const aName = (a && a.name) || '';
                const bName = (b && b.name) || '';
                return aName.localeCompare(bName);
            });
        },
        cardTypes() {
            const types = new Set();
            this.walletCards.forEach((card) => {
                if (card.card?.type) {
                    types.add(card.card.type);
                } else {
                    types.add('Other');
                }
            });

            return Array.from(types)
                .map((type) => ({ id: type, name: type }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        subGroupingOptions(state) {
            return state.groupingOptions.filter(
                (option) => option.id !== state.groupBy?.id
            );
        },
    },
    actions: {
        // Helper method for applying filters
        applyCardFilter(cards, selectedItems, valueGetter) {
            if (!selectedItems || selectedItems.length === 0) {
                return cards;
            }

            const selectedIds = selectedItems.map((item) => item.id);
            return cards.filter((card) => {
                const value = valueGetter(card);
                return value && selectedIds.includes(value);
            });
        },
        applyAllFilters(cardsArr) {
            let cards = [...cardsArr];

            // if (!this.showHiddenCards) {
            //     cards = cards.filter((card) => !card.ignore);
            // }

            if (
                this.selectedUsersOrEntities &&
                this.selectedUsersOrEntities.length > 0
            ) {
                const selectedIds = this.selectedUsersOrEntities.map(
                    (item) => item.id
                );

                cards = cards.filter((card) => {
                    const userId = card.user_id || (card.user && card.user.id);
                    if (
                        !card.business_id &&
                        userId &&
                        selectedIds.includes(userId)
                    ) {
                        return true;
                    }

                    if (card.business_id) {
                        const businessId = `business-${card.business_id}`;

                        if (selectedIds.includes(businessId)) {
                            return true;
                        }
                    }

                    return false;
                });
            }

            cards = this.applyCardFilter(
                cards,
                this.selectedBanks,
                (card) => card.institution && card.institution.issuing_bank_id
            );

            cards = this.applyCardFilter(
                cards,
                this.selectedCardTypes,
                (card) => card.card && card.card.type
            );

            cards = this.applyCardFilter(
                cards,
                this.selectedStatuses,
                (card) => card.card_status
            );

            cards = this.applyCardFilter(
                cards,
                this.selectedAccountTypes,
                (card) => card.account_type
            );

            cards = this.applyCardFilter(
                cards,
                this.selectedWelcomeBonusStatuses,
                (card) => card.welcome_bonus_status
            );

            cards = this.applyCardFilter(
                cards,
                this.selectedBonusOfferStatuses,
                (card) => card.bonus_offer_status
            );

            cards = this.applyCardFilter(
                cards,
                this.selectedFiveTwentyFourStatuses,
                (card) => card.chase_524_status
            );

            return cards;
        },
        groupByCardId(cards) {
            const map = new Map();

            for (const card of cards) {
                const id = card.card_id;
                if (!map.has(id)) {
                    map.set(id, []);
                }
                map.get(id).push(card);
            }

            return Array.from(map.entries()).map(([card_id, cards]) => ({
                card_id,
                cards,
            }));
        },
        resetVisibleColumns() {
            // Reset to show ALL columns by default (excluding 'actions')
            this.visibleColumns = [
                'title',
                'institution_name',
                'card_type',
                'user_name',
                'account_type',
                'credit_limit',
                'card_status',
                'date_opened',
                'date_closed',
                'annual_fee_computed',
                'welcome_bonus',
                'bonus_offers',
                'chase_524',
            ];
            this.saveUserDisplayPreferences();
        },
        setSubgroupBy(option) {
            this.subgroupBy = option;
            this.saveUserDisplayPreferences();
        },

        setShowHiddenCards(bool) {
            this.showHiddenCards = bool;
            this.saveUserDisplayPreferences();
        },
        setShowProductChangeModal(bool) {
            this.showProductChangeModal = bool;
        },
        setEditingProductChange(productChange) {
            this.editingProductChange = productChange;
        },

        // Filter selection actions
        toggleUserSelection(user) {
            const index = this.selectedUsersOrEntities.findIndex(
                (u) => u.id === user.id
            );
            if (index > -1) {
                this.selectedUsersOrEntities.splice(index, 1);
            } else {
                this.selectedUsersOrEntities.push(user);
            }
        },

        toggleVisibleColumn(columnId) {
            const index = this.visibleColumns.indexOf(columnId);
            if (index > -1) {
                this.visibleColumns.splice(index, 1);
            } else {
                this.visibleColumns.push(columnId);
            }

            this._ensureDebouncedSave()();
        },
        toggleAccountTypeSelection(type) {
            const index = this.selectedAccountTypes.findIndex(
                (t) => t.id === type.id
            );
            if (index > -1) {
                this.selectedAccountTypes.splice(index, 1);
            } else {
                this.selectedAccountTypes.push(type);
            }
        },

        toggleBankSelection(bank) {
            const index = this.selectedBanks.findIndex((b) => b.id === bank.id);
            if (index > -1) {
                this.selectedBanks.splice(index, 1);
            } else {
                this.selectedBanks.push(bank);
            }
        },

        toggleCardTypeSelection(type) {
            const index = this.selectedCardTypes.findIndex(
                (t) => t.id === type.id
            );
            if (index > -1) {
                this.selectedCardTypes.splice(index, 1);
            } else {
                this.selectedCardTypes.push(type);
            }
        },

        toggleStatusSelection(status) {
            const index = this.selectedStatuses.findIndex(
                (s) => s.id === status.id
            );
            if (index > -1) {
                this.selectedStatuses.splice(index, 1);
            } else {
                this.selectedStatuses.push(status);
            }
        },
        toggleWelcomeBonusStatusSelection(status) {
            const index = this.selectedWelcomeBonusStatuses.findIndex(
                (s) => s.id === status.id
            );
            if (index > -1) {
                this.selectedWelcomeBonusStatuses.splice(index, 1);
            } else {
                this.selectedWelcomeBonusStatuses.push(status);
            }
        },
        toggleBonusOfferStatusSelection(status) {
            const index = this.selectedBonusOfferStatuses.findIndex(
                (s) => s.id === status.id
            );
            if (index > -1) {
                this.selectedBonusOfferStatuses.splice(index, 1);
            } else {
                this.selectedBonusOfferStatuses.push(status);
            }
        },
        toggleFiveTwentyFourStatusSelection(status) {
            const index = this.selectedFiveTwentyFourStatuses.findIndex(
                (s) => s.id === status.id
            );
            if (index > -1) {
                this.selectedFiveTwentyFourStatuses.splice(index, 1);
            } else {
                this.selectedFiveTwentyFourStatuses.push(status);
            }
        },

        clearAllFilters() {
            this.resetSelectedUserOrEntityFilter();
            this.resetSelectedBankFilter();
            this.resetSelectedCardTypeFilter();
            this.resetSelectedStatusFilter();
            this.resetSelectedAccountTypeFilter();
            this.resetSelectedWelcomeBonusStatusFilter();
            this.resetSelectedBonusOfferStatusFilter();
            this.resetSelectedFiveTwentyFourStatusFilter();
        },
        resetSelectedUserOrEntityFilter() {
            this.selectedUsersOrEntities = [];
        },
        resetSelectedBankFilter() {
            this.selectedBanks = [];
        },
        resetSelectedCardTypeFilter() {
            this.selectedCardTypes = [];
        },
        resetSelectedStatusFilter() {
            this.selectedStatuses = [];
        },
        resetSelectedAccountTypeFilter() {
            this.selectedAccountTypes = [];
        },
        resetSelectedWelcomeBonusStatusFilter() {
            this.selectedWelcomeBonusStatuses = [];
        },
        resetSelectedBonusOfferStatusFilter() {
            this.selectedBonusOfferStatuses = [];
        },
        resetSelectedFiveTwentyFourStatusFilter() {
            this.selectedFiveTwentyFourStatuses = [];
        },
        setWalletEntities(arr) {
            this.walletEntities = arr;
        },
        initWalletCache(userUuid) {
            if (!walletCache && userUuid) {
                walletCache = useWalletCache(userUuid);
            }
            return walletCache;
        },

        /**
         * MY1-2830: entry point for the wallet page. Hydrates instantly from the
         * IndexedDB cache when present (no full-page spinner — the subtle refresh
         * indicator shows instead), then revalidates over the network. Cold with
         * no cache → full-page spinner during the shell fetch.
         */
        async bootstrapWallet(userUuid) {
            const cache = this.initWalletCache(userUuid);
            // Show the cold spinner immediately; the IndexedDB read below is fast
            // and clears it the moment we have cached data to paint.
            this.setLoadingWallet(true);
            const cached = cache ? await cache.hydrate() : null;

            if (cached?.shell) {
                this.hydrateFromCache(cached);
                this.setLoadingWallet(false);
                // MY1-2830: skip the immediate revalidation when the cached shell is
                // only a few seconds old (e.g. a quick back-navigation). Mutations
                // refresh + overwrite the cache on their own, so this just avoids a
                // redundant second render (costly on large wallets) in exchange for
                // very-slightly staler data on rapid revisits.
                const SKIP_REVALIDATE_WITHIN_MS = 10000;
                if ((cached.ageMs ?? Infinity) > SKIP_REVALIDATE_WITHIN_MS) {
                    await this.getWalletByEntities(null, { background: true });
                }
            } else {
                await this.getWalletByEntities();
            }
        },

        /** Apply a shell payload (fresh or cached) to store state. */
        applyShell(data, { refreshMembers = true } = {}) {
            this.setWallet(data.wallet);
            this.setWalletEntities(data.wallet.entities);
            this.walletUsers = data.wallet.users;
            this.processWalletCards(data.wallet);
            this.hasConnections = data.wallet.has_connections;
            this.setWalletId(data.wallet.id);
            this.setCards(data.cards);
            this.setBanks(data.banks);
            this.features = data.features;
            this.setUserDisplayPreferences(
                data.wallet.user_display_preferences
            );

            if (refreshMembers) {
                this.loadMembers();
            }
        },

        /**
         * SUPPORT-739: background-revalidate the shell when it has gone stale.
         * bootstrapWallet only runs on mount, so a long-lived tab renders its
         * mount-time payload indefinitely — the wallet page calls this when the
         * tab regains visibility. No-ops while a load/refresh is in flight,
         * before the first shell arrives, or when the shell is still fresh.
         */
        async revalidateWalletIfStale(maxAgeMs = WALLET_SHELL_MAX_AGE_MS) {
            if (this.loadingWallet || this.refreshingWallet) {
                return false;
            }
            if (
                !this.shellSyncedAt ||
                Date.now() - this.shellSyncedAt < maxAgeMs
            ) {
                return false;
            }
            await this.getWalletByEntities(null, { background: true });
            return true;
        },

        /** Instant paint from cached shell + benefits; no spinner, no network. */
        hydrateFromCache(cached) {
            if (!cached?.shell) {
                return;
            }
            this.applyShell(cached.shell, { refreshMembers: false });
            // Backdate the sync marker by the cache record's age so a tab that
            // hydrates from an old snapshot revalidates on its next focus.
            this.shellSyncedAt = Date.now() - (cached.ageMs ?? 0);
            if (cached.benefits?.benefits_by_account) {
                this.mergeBenefits(cached.benefits.benefits_by_account);
            }
        },

        /**
         * Merge the async benefits payload onto the already-rendered shell cards.
         * walletCards entries are the SAME object references held in walletEntities
         * (processWalletCards pushes them by reference), so mutating in place
         * updates both views with no table rebuild. Keyed by connected_account_id.
         */
        mergeBenefits(benefitsByAccount) {
            if (!benefitsByAccount) {
                return;
            }
            this.walletCards.forEach((account) => {
                const data = benefitsByAccount[account.id];
                if (!data) {
                    return;
                }
                account.benefits_summary = data.benefits_summary;
                if (account.card) {
                    account.card.card_benefits = data.card_benefits;
                }
            });
        },

        /**
         * Two-phase wallet load: fast shell (renders the table) then async
         * benefits (fills in per-card summaries/details). On a cold load the
         * full-page spinner shows during the shell fetch; a background refresh
         * (after a mutation, or revalidating cached data) shows only the subtle
         * indicator and never blanks the table.
         */
        async getWalletByEntities(changedCard = null, { background = false } = {}) {
            if (changedCard) {
                this.selectedCard = changedCard.card;
            }

            if (background) {
                this.refreshingWallet = true;
            } else {
                this.setLoadingWallet(true);
            }

            try {
                // Phase 1 — shell: everything the table needs, no benefits.
                const shellResponse = await ApiService.apiCall(
                    '/wallets/wallet-shell-by-entities',
                    'get'
                );

                this.applyShell(shellResponse.data);
                this.shellSyncedAt = Date.now();
                // Table can render now; drop the cold-load spinner immediately.
                this.setLoadingWallet(false);
                if (walletCache) {
                    walletCache.saveShell(shellResponse.data);
                }

                // Phase 2 — benefits: heavy, merged in when ready. Non-fatal on
                // failure; the table stays functional without per-card totals.
                this.benefitsLoading = true;
                try {
                    const benefitsResponse = await ApiService.apiCall(
                        '/wallets/wallet-benefits',
                        'get'
                    );
                    this.mergeBenefits(
                        benefitsResponse.data.benefits_by_account
                    );
                    if (walletCache) {
                        walletCache.saveBenefits(benefitsResponse.data);
                    }
                } catch (benefitsError) {
                    console.error(
                        'Error fetching wallet benefits:',
                        benefitsError
                    );
                } finally {
                    this.benefitsLoading = false;
                }
            } catch (error) {
                console.error('Error fetching wallet:', error);
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'There was a problem trying to get your wallet. Please try again later.',
                    },
                    4000
                );
            } finally {
                this.setLoadingWallet(false);
                this.refreshingWallet = false;
            }
        },
        // Original wallet actions - keeping for backwards compatibility but can eventually remove
        getWallet() {
            this.setLoadingWallet(true);
            return ApiService.apiCall('/wallets', 'get')
                .then((response) => {
                    this.setWallet(response.data.wallet);
                    this.hasConnections = response.data.wallet.has_connections;
                    this.setWalletId(response.data.wallet.id);
                    this.setUsers(response.data.wallet.users);
                    this.setCards(response.data.cards);
                    this.setBanks(response.data.banks);
                    this.features = response.data.features;
                    this.setUserDisplayPreferences(
                        response.data.wallet.user_display_preferences
                    );

                    // if (response.data.wallet.unresolved_cards.length > 0) {
                    //     this.unmatchCount =
                    //         response.data.wallet.unresolved_cards.length;
                    //     if (
                    //         !this.$cookies.get('matchaccounts') ||
                    //         response.data.wallet.has_new_accounts
                    //     ) {
                    //         this.showUnmatchedModal = true;
                    //     }
                    // }
                })
                .catch((error) => {
                    console.error('Error fetching wallet:', error);
                    // Handle specific error responses or UI updates here if needed
                    // For example, you might want to show a toast notification to the user
                    // this.$root.handleHttpStatus(error.response); // If you have a global error handler
                })
                .finally(() => {
                    this.setLoadingWallet(false);
                });
        },

        getCards() {
            return ApiService.apiCall('/wallets/cards', 'get')
                .then((response) => {
                    this.setCards(response.data.cards);
                    return response.data.cards;
                })
                .catch((_) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get your cards.',
                        },
                        4000
                    );
                    return [];
                });
        },
        setWallet(data) {
            this.wallet = data;
        },
        setBusinessEntity(data) {
            this.businessEntity = data;
        },
        setShowBusinessAddEditModal(bool) {
            this.showBusinessAddEditModal = bool;
        },
        setShowManageBusinessesModal(bool) {
            this.showManageBusinessesModal = bool;
        },
        setShowDeleteConfirmModal(bool) {
            this.showDeleteConfirmModal = bool;
        },
        // MY1-3044: see connectModalRequested in state.
        requestConnectModal() {
            this.connectModalRequested = true;
        },

        setWalletId(data) {
            this.walletId = data;
        },
        saveUserDisplayPreferences() {
            if (!this.wallet.id) return;

            const preferences = {
                sortBy: this.sortBy?.id || null,
                sortDirection: this.sortDirection,
                groupBy: this.groupBy?.id || null,
                subgroupBy: this.subgroupBy?.id || null,
                visibleColumns: this.visibleColumns,
                showLastFour: this.showLastFour,
                showHiddenCards: this.showHiddenCards,
                groupSameCards: this.groupSameCards,
                columnOrder: this.columnOrder,
            };

            ApiService.apiCall(
                `/wallets/${this.wallet.id}/display-preferences`,
                'put',
                preferences
            )
                .then((response) => {
                    if (response.data && response.data.preferences) {
                    }
                })
                .catch((error) => {
                    console.error('Failed to save display preferences:', error);
                });
        },
        setUserDisplayPreferences(preferences) {
            if (!preferences) return;

            if (preferences.sortBy) {
                this.sortDirection = preferences.sortDirection;
                this.sortBy = preferences.sortBy;
            }

            // Improved null handling for groupBy (from Codegen PR #49)
            if (preferences.hasOwnProperty('groupBy')) {
                if (preferences.groupBy === null) {
                    this.groupBy = null;
                } else {
                    this.groupBy =
                        this.groupingOptions.find(
                            (o) => o.id === preferences.groupBy
                        ) || null;
                }
            }

            // Improved null handling for subgroupBy (from Codegen PR #49)
            if (preferences.hasOwnProperty('subgroupBy')) {
                if (preferences.subgroupBy === null) {
                    this.subgroupBy = null;
                } else {
                    this.subgroupBy =
                        this.groupingOptions.find(
                            (o) => o.id === preferences.subgroupBy
                        ) || null;
                }
            }

            if (preferences.visibleColumns?.length > 0) {
                this.visibleColumns = [...preferences.visibleColumns];
            }
            if (preferences.showLastFour !== undefined) {
                this.showLastFour = preferences.showLastFour;
            }
            if (preferences.groupSameCards !== undefined) {
                this.groupSameCards = preferences.groupSameCards;
            }
            if (preferences.showHiddenCards !== undefined) {
                this.showHiddenCards = preferences.showHiddenCards;
            }
            if (preferences.columnOrder !== undefined) {
                this.columnOrder = preferences.columnOrder;
            }
        },
        setInstitution(data) {
            this.instituion = data;
        },
        setUsers(data) {
            this.users = data;
        },
        setCards(data) {
            this.cards = data;
        },
        _ensureDebouncedSave() {
            if (!this._debouncedSavePreferences) {
                const { debounce } = useDebounce();
                this._debouncedSavePreferences = debounce(() => {
                    this.saveUserDisplayPreferences();
                }, 500);
            }
            return this._debouncedSavePreferences;
        },

        loadMembers() {
            ApiService.apiCall('/wallets/members', 'get')
                .then((response) => {
                    let allMembers = [];

                    // Add main user's members
                    if (response?.data?.members?.length) {
                        allMembers = [...(response.data.members || [])];
                    }

                    // Add shared users' members for connection status matching
                    if (response?.data?.shared_users?.length) {
                        response.data.shared_users.forEach((sharedUser) => {
                            if (sharedUser?.members?.length) {
                                allMembers = [
                                    ...allMembers,
                                    ...(sharedUser.members || []),
                                ];
                            }
                        });
                    }

                    this.members = allMembers;
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    notify(
                        {
                            group: 'error',
                            title: 'Connection Error',
                            text: 'Failed to load connection data. Some features may not work correctly.',
                        },
                        4000
                    );
                    this.members = [];
                });
        },

        getConnectionStatusForCard(card) {
            if (!card?.member_guid || !this.members?.length) {
                return null;
            }

            const member = this.members.find(
                (m) => m?.guid === card.member_guid
            );
            return member?.connection_status || null;
        },

        hasConnectionProblem(connectionStatus) {
            if (!connectionStatus) return false;
            return CONNECTION_PROBLEM_STATES.includes(connectionStatus);
        },

        getConnectionProblemForCard(card) {
            const status = this.getConnectionStatusForCard(card);
            return this.hasConnectionProblem(status) ? status : null;
        },

        setGroupSameCards(bool) {
            this.groupSameCards = bool;
            this._ensureDebouncedSave()();
        },
        setShowLastFour(bool) {
            this.showLastFour = bool;
            this._ensureDebouncedSave()();
        },
        setAccountSidebar(show) {
            if (show) {
                this.showAccountSidebar = show;
            } else {
                if (
                    this.selectedCard &&
                    typeof this.selectedCard === 'object' &&
                    'sidebarOpen' in this.selectedCard
                ) {
                    this.selectedCard.sidebarOpen = false;
                }

                this.showAccountSidebar = show;

                const currentTimestamp = Date.now();
                this.lastClosedTimestamp = currentTimestamp;

                setTimeout(() => {
                    if (
                        !this.showAccountSidebar &&
                        this.lastClosedTimestamp === currentTimestamp &&
                        !this.showCopyCardModal &&
                        !this.showEditCardModal
                    ) {
                        this.selectedCard = null;
                    }
                }, 1000);
            }
        },
        setGroupBy(val) {
            this.groupBy = val;
        },
        setLoadingWallet(bool) {
            this.loadingWallet = bool;
        },
        setBanks(data) {
            this.banks = data;
        },
        setShowAddUserModal(val) {
            this.showAddUserModal = val;
        },
        setShowEditCardModal(val) {
            this.showEditCardModal = val;
        },
        setSelectedCard(val) {
            this.selectedCard = val;
        },
        setSelectedUser(val) {
            this.selectedUser = val;
        },
        // Show a notification with the specified parameters
        // group: The notification group (success, error, etc)
        // title: The notification title
        // text: The notification text content
        // duration: Duration in milliseconds (default: 8000)
        showNotification(title, text, duration = 8000, group = 'success') {
            notify(
                {
                    group,
                    title,
                    text,
                },
                duration
            );
        },

        deleteCard(card) {
            // Guard against undefined or null card
            if (!card || !card.id) {
                console.error('deleteCard: Invalid card object provided', card);
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Unable to delete card: Invalid card data',
                    },
                    6000
                );
                return;
            }

            ApiService.apiCall(
                // APP-329: a member's card can show in this wallet while its row
                // lives in their own wallet; the API wants the card's wallet.
                '/wallets/' + card.wallet_id + '/cards/' + card.id,
                'delete'
            )
                .then((response) => {
                    if (response.status === 200) {
                        notify(
                            {
                                group: 'success',
                                title: 'Success',
                                text: 'Your card has been removed from your wallet!',
                            },
                            6000
                        );
                        // Background refresh: table stays visible, subtle indicator only.
                        this.getWalletByEntities(null, { background: true });
                    }
                })
                .catch((response) => {
                    console.error(response);
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'Failed to delete card',
                        },
                        6000
                    );
                });
        },
        setSortBy(direction = 'asc', column) {
            this.sortBy = column;
            this.setSortDirection(direction);
        },
        setSortDirection(direction) {
            this.sortDirection = direction;
        },
        updateCard(data, id) {
            return ApiService.apiCall(
                '/wallets/' + this.wallet.id + '/cards/' + id,
                'put',
                data
            ).then((response) => {
                return response;
            });
        },

        updateCardVisibility(id, ignore) {
            return ApiService.apiCall(
                '/wallets/' + this.wallet.id + '/cards/' + id + '/visibility',
                'put',
                { ignore }
            ).then((response) => {
                return response;
            });
        },

        copyCardDetails(data, id) {
            return ApiService.apiCall(
                '/wallets/' + this.wallet.id + '/cards/' + id + '/copy',
                'put',
                data
            ).then((response) => {
                return response;
            });
        },
        setShowCopyCardModal(show) {
            this.showCopyCardModal = show;
        },

        getPendingInvitations() {
            return ApiService.apiCall('/wallets/invitations', 'get')
                .then((response) => {
                    let invitationsData = [];

                    if (response.data && response.data.wallet_invitations) {
                        invitationsData =
                            response.data.wallet_invitations || [];
                    } else if (
                        response.data &&
                        response.data.user_invitations
                    ) {
                        invitationsData = response.data.user_invitations || [];
                    } else if (Array.isArray(response.data)) {
                        invitationsData = response.data;
                    } else {
                        console.warn(
                            'No invitations data found in API response'
                        );
                    }

                    this.pendingInvitations = invitationsData.map(
                        (invitation) => {
                            if (invitation.wallet && invitation.wallet.user) {
                                return {
                                    ...invitation,
                                    from_user:
                                        invitation.wallet.user.name || 'User',
                                    from_email:
                                        invitation.wallet.user.email ||
                                        'email@example.com',
                                };
                            } else if (invitation.from_user) {
                                return invitation;
                            } else {
                                return {
                                    ...invitation,
                                    from_user: 'User',
                                    from_email: 'user@company.com',
                                };
                            }
                        }
                    );

                    return response;
                })
                .catch((error) => {
                    console.error('Error fetching pending invitations:', error);

                    this.pendingInvitations = [
                        {
                            guid: 'test-invitation-guid',
                            from_user: 'User',
                            from_email: 'user@company.com',
                            wallet_name: 'Family Wallet',
                        },
                    ];

                    return { data: this.pendingInvitations };
                });
        },

        acceptInvitation(guid) {
            if (!guid) return Promise.reject('No invitation GUID provided');

            return fetch(`/settings/invitations/${guid}/accept`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document
                        .querySelector('meta[name="csrf-token"]')
                        .getAttribute('content'),
                },
                credentials: 'same-origin',
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(
                            `Error ${response.status}: ${response.statusText}`
                        );
                    }
                    return response.json();
                })
                .then((data) => {
                    // Actualizar las invitaciones pendientes y el wallet
                    this.getPendingInvitations();
                    this.getWallet();
                    return data;
                })
                .catch((error) => {
                    console.error('Error accepting invitation:', error);
                    throw error;
                });
        },

        rejectInvitation(guid) {
            if (!guid) return Promise.reject('No invitation GUID provided');

            return fetch(`/settings/invitations/${guid}/request-delete`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document
                        .querySelector('meta[name="csrf-token"]')
                        .getAttribute('content'),
                },
                credentials: 'same-origin',
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(
                            `Error ${response.status}: ${response.statusText}`
                        );
                    }
                    return response.json();
                })
                .then((data) => {
                    // Actualizar solo las invitaciones pendientes
                    this.getPendingInvitations();
                    return data;
                })
                .catch((error) => {
                    console.error('Error rejecting invitation:', error);
                    throw error;
                });
        },

        // Método para gestionar el estado del banner de invitación
        dismissInvitationBanner() {
            this.invitationBannerDismissed = true;
        },

        processWalletCards(data) {
            this.walletCards = data.entities.reduce((acc, currEntity) => {
                currEntity.institutions.forEach((institution) => {
                    institution.accounts.forEach((account) => {
                        account['user_name'] = currEntity.name;
                        account['is_business'] = currEntity.is_business;
                        account['institution_name'] = institution?.issuing_bank
                            ?.name
                            ? institution.issuing_bank.name
                            : 'Unknown';
                        account['card_title'] =
                            account?.card?.title && !account.card_match_request
                                ? account.card.title
                                : account.name || 'Unmatched card';
                        account['annual_fee_computed'] =
                            account.user_managed_annual_fee
                                ? account.annual_fee
                                : account.card?.annual_fee;
                        acc.push(account);
                    });
                });
                return acc;
            }, []);
        },
        sortCards(arr) {
            return [...arr].sort((a, b) => {
                try {
                    let aVal = a[this.sortBy] ?? '';
                    let bVal = b[this.sortBy] ?? '';

                    if (this.sortBy.includes('date') && aVal && bVal) {
                        if (
                            typeof aVal === 'string' &&
                            typeof bVal === 'string'
                        ) {
                            try {
                                const aDate = dayjs(aVal, 'MM/DD/YYYY');
                                const bDate = dayjs(bVal, 'MM/DD/YYYY');

                                if (aDate.isValid() && bDate.isValid()) {
                                    aVal = aDate.valueOf();
                                    bVal = bDate.valueOf();
                                }
                            } catch (err) {
                                console.error(
                                    'Error parsing dates with dayjs:',
                                    err
                                );
                            }
                        }
                    } else if (
                        (typeof aVal === 'number' ||
                            !isNaN(parseFloat(aVal))) &&
                        (typeof bVal === 'number' || !isNaN(parseFloat(bVal)))
                    ) {
                        aVal =
                            typeof aVal === 'number'
                                ? aVal
                                : parseFloat(aVal) || 0;
                        bVal =
                            typeof bVal === 'number'
                                ? bVal
                                : parseFloat(bVal) || 0;
                    } else if (
                        typeof aVal === 'string' &&
                        typeof bVal === 'string'
                    ) {
                        aVal = aVal.toLowerCase();
                        bVal = bVal.toLowerCase();
                    }

                    const direction = this.sortDirection === 'asc' ? 1 : -1;
                    return (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) * direction;
                } catch (err) {
                    console.error('Error sorting cards:', err);
                    return 0;
                }
            });
        },
        getBusinessEntitiesByUser() {
            this.loadingBusinessEntities = true;
            return ApiService.apiCall('/businesses/users', 'get')
                .then((response) => {
                    this.businessEntities = response.data;
                })
                .catch((error) => {
                    // SUPPORT-739: never reject — this runs fire-and-forget
                    // from the manage modal's open watcher and inside the 404
                    // recovery path, where a rejection would turn a graceful
                    // "already removed" resolution back into a stuck dialog.
                    // Keep whatever list is already rendered.
                    console.error(
                        'Error fetching business entities:',
                        error
                    );
                })
                .finally(() => {
                    this.loadingBusinessEntities = false;
                });
        },
        /**
         * SUPPORT-739: a business row rendered from a stale wallet payload
         * (long-lived tab, IndexedDB snapshot) can reference an entity that no
         * longer exists, so a delete/update on it 404s. Converge every surface
         * back onto the database: drop the client cache and refresh both the
         * wallet payload and the fresh business entities list in the background.
         */
        async refreshAfterStaleBusinessReference() {
            if (walletCache) {
                await walletCache.invalidate();
            }
            await Promise.all([
                this.getWalletByEntities(null, { background: true }),
                this.getBusinessEntitiesByUser(),
            ]);
        },
        async deleteBusiness(businessData) {
            try {
                const response = await ApiService.apiCall(
                    `/businesses/${businessData.uuid}/delete`,
                    'delete'
                );

                if (response.status === 200) {
                    notify(
                        {
                            group: 'success',
                            title: 'Success',
                            text: 'Business deleted successfully',
                        },
                        8000
                    );
                } else {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: response.message,
                        },
                        8000
                    );
                }
                return response;
            } catch (error) {
                // SUPPORT-739: 404 means the entity was already deleted and the
                // row on screen was stale. Resolve instead of erroring so the
                // caller's normal post-delete flow (close modal, refetch) runs.
                if (error.response?.status === 404) {
                    notify(
                        {
                            group: 'success',
                            title: 'Already removed',
                            text: 'That business entity no longer exists. Refreshing your list.',
                        },
                        8000
                    );
                    await this.refreshAfterStaleBusinessReference();
                    return null;
                }
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text:
                            error.response?.data?.message ||
                            'Failed to delete business',
                    },
                    8000
                );
                throw error;
            }
        },
        async addBusiness(business) {
            try {
                const response = await ApiService.apiCall(
                    '/businesses',
                    'post',
                    {
                        user_id: business.userId,
                        name: business.name,
                        type: business.type,
                        tax_id_type: business.tax_id_type,
                        annual_business_spend: business.annual_business_spend
                            ? parseFloat(
                                  business.annual_business_spend
                                      .toString()
                                      .replace(/,/g, '')
                              )
                            : null,
                    }
                );
                if (response.status === 200) {
                    notify(
                        {
                            group: 'success',
                            title: 'Success',
                            text: 'Business added successfully',
                        },
                        4000
                    );
                    return response;
                }
            } catch (error) {
                console.error(error);
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: error.message,
                    },
                    8000
                );
                return error;
            }
        },
        async updateBusiness(business) {
            try {
                const response = await ApiService.apiCall(
                    `/businesses/${business.uuid}`,
                    'put',
                    {
                        name: business.name,
                        type: business.type,
                        tax_id_type: business.tax_id_type,
                        annual_business_spend: business.annual_business_spend
                            ? parseFloat(
                                  business.annual_business_spend
                                      .toString()
                                      .replace(/,/g, '')
                              )
                            : null,
                    }
                );

                if (response.status === 200) {
                    notify(
                        {
                            group: 'success',
                            title: 'Success',
                            text: 'Business updated successfully',
                        },
                        5000
                    );
                    return response.data.business;
                } else {
                    throw new Error(
                        response.message || 'Failed to update business'
                    );
                }
            } catch (error) {
                this.setLoadingWallet(false);
                // SUPPORT-739: editing a stale row whose entity was already
                // deleted — resolve (close + refresh) rather than error.
                if (error.response?.status === 404) {
                    notify(
                        {
                            group: 'success',
                            title: 'Already removed',
                            text: 'That business entity no longer exists. Refreshing your list.',
                        },
                        5000
                    );
                    await this.refreshAfterStaleBusinessReference();
                    return null;
                }
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text:
                            error.response?.data?.message ||
                            'Failed to update business',
                    },
                    5000
                );
                throw error;
            }
        },
        resetBusinessEntity() {
            this.businessEntity = {
                id: null,
                user_id: null,
                name: null,
                type: null,
                tax_id_type: null,
            };
        },
        updateColumnOrder(newOrder) {
            // Update the column order based on the new arrangement
            if (
                !newOrder ||
                !Array.isArray(newOrder) ||
                newOrder.length === 0
            ) {
                // Invalid input - don't update order
                return;
            }

            // Validate that all items have valid IDs
            const newColumnOrder = newOrder
                .filter((col) => col && col.id) // Ensure valid column objects
                .map((col) => col.id);

            if (newColumnOrder.length > 0) {
                // Ensure all visible columns are included in the new order
                // The orderedColumns getter already handles missing columns (lines 178-182)
                // by adding any columns not in the order array for backwards compatibility
                this.columnOrder = newColumnOrder;
                // Save preferences immediately
                this.saveUserDisplayPreferences();
            }
        },
        setColumnOrder(columnOrder) {
            this.columnOrder = columnOrder;
        },
        resetColumnOrder() {
            // Reset to default column order (based on original columns array)
            if (!this.columns || this.columns.length === 0) {
                // If no columns available, reset to empty array
                this.columnOrder = [];
                return;
            }

            // Create default order from all available columns to ensure future-proofing
            // This will automatically include any new columns added to the system
            const defaultOrder = this.columns
                .filter((col) => col && col.id) // Ensure valid column objects
                .map((col) => col.id);

            this.columnOrder = [...defaultOrder];

            // Save preferences immediately
            this.saveUserDisplayPreferences();
        },
        openCardMatchModal(card) {
            if (card) {
                this.setSelectedCard(card);
            }
            this.setShowCardMatchModal(true);
        },
        // Open the card-match modal directly on the "Missing Card Request"
        // form (step 2), skipping the card-search step. Used by notification
        // deep-links (NoCardMatch / RejectedResolvedAccount) where the user
        // is being routed back specifically to file/refile a request.
        openCardMatchModalAtRequestForm(card) {
            if (card) {
                this.setSelectedCard(card);
            }
            this.cardMatchSkipToRequestForm = true;
            this.setShowCardMatchModal(true);
        },
        setShowCardMatchModal(value) {
            this.showCardMatchModal = value;
            // Always clear the skip flag once the modal closes so the next
            // open via the normal flow starts on step 1 again.
            if (!value) {
                this.cardMatchSkipToRequestForm = false;
            }
        },
        // Handle new invitations
        setNewInvitation(invitation) {
            this.newInvitation = invitation;
        },
        clearNewInvitation() {
            this.newInvitation = null;
        },
        setActiveAccountDropdown(value) {
            this.activeAccountDropdown = value;
        },
        setShowCardDropdown(card, el) {
            this.showCardDropdown = true;
            this.activeAccountDropdownElement = el;
            this.activeAccountDropdown = card;
        },
    },
});
